/**
 * Payment Receipt Generator Utility
 *
 * A reusable utility for generating beautiful, standardized payment receipts
 * for University of Chittagong Admission System.
 *
 * Usage:
 * import { generateReceipt } from '@/utils/ReceiptGenerator'
 *
 * generateReceipt({
 *   receiptType: 'admission',
 *   studentInfo: { name, fatherName, userID },
 *   paymentInfo: { billNumber, amount, transactionId, paidAt, method, unit },
 *   additionalInfo: { approvalCode, status }
 * })
 */

import { jsPDF } from "jspdf";

/**
 * Default configuration for receipt generation
 */
const DEFAULT_CONFIG = {
  university: "University of Chittagong",
  academicYear: "2026-2027",
  documentTitle: "Admission Test 2026-2027",
  logoPath: require("@/theme-assets/images/logo/cu_logo.png"),
  primaryColor: "#2c3e50",
  accentColor: "#3498db",
  successColor: "#27ae60",
  fontSize: {
    title: 24,
    subtitle: 18,
    heading: 16,
    body: 12,
    small: 10,
  },
};

/**
 * Formats date string to a more readable format
 */
function formatDate(dateString) {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

/**
 * Formats amount with BDT currency
 */
function formatAmount(amount) {
  if (!amount) return "N/A";
  return `${parseFloat(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} BDT`;
}

/**
 * Generate HTML template for receipt
 */
function generateReceiptHTML(data, config = DEFAULT_CONFIG) {
  const {
    studentInfo = {},
    paymentInfo = {},
    additionalInfo = {},
  } = data;

  const logoDataUrl = config.logoPath;

  return `
    <div style="font-family: Arial, sans-serif; max-width: 100%; margin: 0; padding: 0; background: white; border: 2px solid #ddd;">

      <!-- Header Banner with Logo -->
      <div style="background:#4e73a8; padding: 18px 15px; border-bottom: 4px solid #4e73a8;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="width: 15%; vertical-align: middle; text-align: center;">
              <div style="width: 55px; height: 55px; background: white; border-radius: 50%; border: 3px solid white; display: inline-block;">
                <div style="color: #4e73a8; font-size: 28px; font-weight: 900; font-family: Arial, sans-serif; line-height: 55px; text-align: center;">CU</div>
              </div>
            </td>
            <td style="width: 70%; vertical-align: middle; text-align: center; padding: 0 10px;">
              <div style="color: white; font-size: 20px; margin: 0 0 5px 0; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase;">
                ${config.university.toUpperCase()}
              </div>
              <div style="color: white; font-size: 14px; font-weight: 600; letter-spacing: 0.8px;">
                ${config.documentTitle}
              </div>
            </td>
            <td style="width: 15%; text-align: center; vertical-align: middle;">
              ${additionalInfo.status ? `
              <div style="background: #27ae60; color: white; display: inline-block; padding: 6px 12px; border-radius: 4px; border: 2px solid white; font-weight: 700; font-size: 11px; letter-spacing: 0.5px;">
                ${additionalInfo.status.toUpperCase()}
              </div>
              ` : ""}
            </td>
          </tr>
        </table>
      </div>

      <!-- Receipt Title Bar -->
      <div style="background: #4e73a8; color: white; padding: 10px 15px; text-align: center;">
        <div style="font-size: 18px; font-weight: 700; letter-spacing: 1.5px;">
          PAYMENT RECEIPT
        </div>
      </div>

      <!-- Main Content: Student Info and Payment Details Side by Side -->
      <div style="padding: 15px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <!-- Student Information Column -->
            <td style="width: 48%; vertical-align: top; padding-right: 10px;">
              <div style="background: #f8f9fa; padding: 5px 10px; margin-bottom: 8px; border-left: 3px solid #4e73a8;">
                <div style="color: #4e73a8; font-size: 11px; font-weight: 700; letter-spacing: 0.5px;">
                  STUDENT INFORMATION
                </div>
              </div>
              <table style="width: 100%; border-collapse: collapse;">
                ${studentInfo.name ? `<tr>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 600;">Name:</td>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 500;">${studentInfo.name}</td>
                </tr>` : ""}
                ${studentInfo.fatherName ? `<tr>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 600;">Father:</td>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 500;">${studentInfo.fatherName}</td>
                </tr>` : ""}
                ${studentInfo.userID ? `<tr>
                  <td style="padding: 5px 8px; color: #4e73a8; font-size: 10px; font-weight: 600;">User ID:</td>
                  <td style="padding: 5px 8px; color: #4e73a8; font-size: 10px; font-weight: 500; font-family: monospace;">${studentInfo.userID}</td>
                </tr>` : ""}
              </table>
            </td>

            <!-- Spacing -->
            <td style="width: 4%;"></td>

            <!-- Payment Details Column -->
            <td style="width: 48%; vertical-align: top; padding-left: 10px;">
              <div style="background: #f8f9fa; padding: 5px 10px; margin-bottom: 8px; border-left: 3px solid #4e73a8;">
                <div style="color: #4e73a8; font-size: 11px; font-weight: 700; letter-spacing: 0.5px;">
                  PAYMENT DETAILS
                </div>
              </div>
              <table style="width: 100%; border-collapse: collapse;">
                ${paymentInfo.unit ? `<tr>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 600; width: 35%;">Unit:</td>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 600;">${paymentInfo.unit}</td>
                </tr>` : ""}
                ${paymentInfo.billNumber ? `<tr>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 600;">Bill No:</td>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 500; font-family: monospace;">${paymentInfo.billNumber}</td>
                </tr>` : ""}
                ${paymentInfo.amount ? `<tr style="background: #e7f3ff;">
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 600;">Amount:</td>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 12px; font-weight: 700;">${formatAmount(paymentInfo.amount)}</td>
                </tr>` : ""}
                ${paymentInfo.transactionId ? `<tr>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 600;">Txn ID:</td>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 9px; font-weight: 500; font-family: monospace;">${paymentInfo.transactionId}</td>
                </tr>` : ""}
                ${additionalInfo.approvalCode ? `<tr>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 600;">Approval:</td>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 9px; font-weight: 500; font-family: monospace;">${additionalInfo.approvalCode}</td>
                </tr>` : ""}
                ${paymentInfo.paidAt ? `<tr>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 600;">Date/Time:</td>
                  <td style="padding: 5px 8px; border-bottom: 1px solid #e9ecef; color: #4e73a8; font-size: 10px; font-weight: 500;">${formatDate(paymentInfo.paidAt)}</td>
                </tr>` : ""}
                ${paymentInfo.method ? `<tr>
                  <td style="padding: 5px 8px; color: #4e73a8; font-size: 10px; font-weight: 600;">Method:</td>
                  <td style="padding: 5px 8px; color: #4e73a8; font-size: 10px; font-weight: 600;">${paymentInfo.method}</td>
                </tr>` : ""}
              </table>
            </td>
          </tr>
        </table>
      </div>

      <!-- Footer -->
      <div style="border-top: 2px solid #ddd; margin-top: 15px; padding: 12px 15px; background: #f8f9fa;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="width: 70%; vertical-align: middle;">
              <div style="color: #4e73a8; font-size: 9px; line-height: 1.5;">
                <div style="margin-bottom: 3px;">
                  <strong style="color: #4e73a8;">Note:</strong>
                  This is a computer-generated receipt. No signature required.
                </div>
                <div style="color: #4e73a8; font-size: 8px; opacity: 0.7;">
                  Keep this receipt for your records and future reference.
                </div>
              </div>
            </td>
            <td style="width: 30%; text-align: right; vertical-align: middle;">
              <div style="color: #4e73a8; font-size: 8px; line-height: 1.4;">
                <div style="font-weight: 600;">${config.university}</div>
                <div>${config.academicYear}</div>
                <div style="opacity: 0.7; margin-top: 2px;">${new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}</div>
              </div>
            </td>
          </tr>
        </table>
      </div>
    </div>
  `;
}

/**
 * Generate and download PDF receipt
 *
 * @param {Object} data - Receipt data
 * @param {string} data.receiptType - Type of receipt (admission, payment, etc.)
 * @param {Object} data.studentInfo - Student information
 * @param {string} data.studentInfo.name - Student name
 * @param {string} data.studentInfo.fatherName - Father's name
 * @param {string} data.studentInfo.userID - User ID
 * @param {Object} data.paymentInfo - Payment information
 * @param {string} data.paymentInfo.billNumber - Bill number
 * @param {string|number} data.paymentInfo.amount - Payment amount
 * @param {string} data.paymentInfo.transactionId - Transaction ID
 * @param {string} data.paymentInfo.paidAt - Payment date/time
 * @param {string} data.paymentInfo.method - Payment method (bKash, Rocket, etc.)
 * @param {string} data.paymentInfo.unit - Academic unit
 * @param {Object} data.additionalInfo - Additional information
 * @param {string} data.additionalInfo.approvalCode - Approval code
 * @param {string} data.additionalInfo.status - Payment status
 * @param {Object} config - Custom configuration (optional)
 * @param {string} filename - PDF filename (optional)
 *
 * @returns {Promise<void>}
 */
export async function generateReceipt(data, config = {}, filename = null) {
  // console.log("generateReceipt called with data:", data);

  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  // Generate HTML content
  const htmlContent = generateReceiptHTML(data, mergedConfig);
  // console.log("HTML content generated, length:", htmlContent.length);

  // Create temporary div with fixed dimensions
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;
  tempDiv.style.cssText = "position:fixed;left:0;top:0;width:550px;padding:25px;background:white;z-index:9999;";
  document.body.appendChild(tempDiv);

  // console.log("Temp div added to body, children count:", tempDiv.children.length);

  // Default filename
  const billNumber = data.paymentInfo && data.paymentInfo.billNumber
    ? data.paymentInfo.billNumber
    : Date.now();
  const defaultFilename = `cu_payment_receipt_${billNumber}.pdf`;
  const finalFilename = filename || defaultFilename;

  return new Promise((resolve, reject) => {
    try {
      // Wait for content to render
      setTimeout(() => {
        try {
          // Generate PDF
          const doc = new jsPDF("p", "pt", "a4");

          doc.html(tempDiv, {
            callback: function (pdf) {
              try {
                pdf.save(finalFilename);
                if (document.body.contains(tempDiv)) {
                  document.body.removeChild(tempDiv);
                }
                resolve();
              } catch (err) {
                console.error("Error saving PDF:", err);
                if (document.body.contains(tempDiv)) {
                  document.body.removeChild(tempDiv);
                }
                reject(err);
              }
            },
            x: 25,
            y: 25,
            width: 545,
            windowWidth: 600,
          });
        } catch (err) {
          console.error("Error generating PDF:", err);
          if (document.body.contains(tempDiv)) {
            document.body.removeChild(tempDiv);
          }
          reject(err);
        }
      }, 250);
    } catch (error) {
      console.error("Error in generateReceipt:", error);
      if (document.body.contains(tempDiv)) {
        document.body.removeChild(tempDiv);
      }
      reject(error);
    }
  });
}

/**
 * Generate receipt HTML for preview (without PDF generation)
 * Useful for displaying receipt in a modal before downloading
 */
export function getReceiptHTML(data, config = {}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  return generateReceiptHTML(data, mergedConfig);
}

/**
 * Preset configurations for different payment methods
 */
export const RECEIPT_PRESETS = {
  BKASH: {
    method: "bKash",
    accentColor: "#E2136E",
  },
  ROCKET: {
    method: "DBBL Rocket",
    accentColor: "#8B3A8B",
  },
  VISA: {
    method: "DBBL Visa",
    accentColor: "#1434CB",
  },
  MASTERCARD: {
    method: "DBBL MasterCard",
    accentColor: "#EB001B",
  },
  NEXUS: {
    method: "DBBL Nexus",
    accentColor: "#2c3e50",
  },
  SURECASH: {
    method: "Surecash",
    accentColor: "#FF6B35",
  },
  BANK: {
    method: "Bank Payment",
    accentColor: "#2c3e50",
  },
};

/**
 * Helper function to prepare data from old format
 */
export function prepareReceiptDataFromLegacy({
  name = "",
  father_name = "",
  userID = "",
  unit = "",
  bill_number = "",
  amount = "",
  txn_id = "",
  paid_at = "",
  agent = "",
  approval = "",
  status = "PAID",
}) {
  return {
    receiptType: "admission",
    studentInfo: {
      name: name,
      fatherName: father_name,
      userID: userID,
    },
    paymentInfo: {
      unit: unit,
      billNumber: bill_number,
      amount: amount,
      transactionId: txn_id,
      paidAt: paid_at,
      method: agent,
    },
    additionalInfo: {
      approvalCode: approval,
      status: status,
    },
  };
}

export default {
  generateReceipt,
  getReceiptHTML,
  RECEIPT_PRESETS,
  prepareReceiptDataFromLegacy,
};
